// 낱말 시각(words) — 전사 발화의 낱말을 본문 글자 위치에 맞추고, 재생 위치의 «지금 낱말» 을 찾는 순수 함수.
// 상세 읽기 모드가 재생 위치가 든 발화의 지금 낱말만 칠한다(S4, 2026-10-07).
// 낱말 항목 모양은 서버(S3)와 같다: [시작초, 끝초, "낱말"].

const WS = /\s/;

// 키 = words 배열(🔴 그래서 words 와 그 항목을 고치지 않는다) · 값 = { text, spans }.
// timeupdate 마다 불리고, 화자 없는 긴 노트는 발화 하나에 낱말이 수만 개일 수 있다.
const cache = new WeakMap();

const isWordEntry = (w) => Array.isArray(w)
  && w.length >= 3
  && Number.isFinite(w[0])
  && Number.isFinite(w[1])
  && typeof w[2] === 'string';

const matchSpans = (text, words) => {
  const spans = [];
  let pos = 0;
  for (let k = 0; k < words.length; k += 1) {
    const w = words[k];
    if (!isWordEntry(w)) return null;
    const label = w[2];
    let from = -1;
    let to = -1;
    for (let j = 0; j < label.length; j += 1) {
      const ch = label[j];
      if (WS.test(ch)) continue;
      while (pos < text.length && WS.test(text[pos])) pos += 1;
      if (pos >= text.length || text[pos] !== ch) return null;
      if (from < 0) from = pos;
      pos += 1;
      to = pos;
    }
    if (from < 0) return null;   // 공백을 빼면 빈 낱말
    spans.push({ from, to, start: w[0], end: w[1] });
  }
  // 낱말을 다 썼는데 공백 아닌 글자가 남으면 일부만 맞는 것 — 칠하지 않는다.
  for (; pos < text.length; pos += 1) {
    if (!WS.test(text[pos])) return null;
  }
  return spans;
};

/**
 * @param {string} text 발화 본문
 * @param {Array} words [[시작초, 끝초, "낱말"], …]
 * @returns {Array<{from: number, to: number, start: number, end: number}>|null}
 *   from·to 는 text 의 UTF-16 인덱스(to 는 끝 다음). 공백류는 양쪽 다 건너뛰고 글자 그대로 비교한다.
 *   한 글자라도 다르거나 · 글자나 낱말이 남거나 · 항목 모양이 틀리면 null(예외 없음).
 *   같은 words 배열·같은 text 면 지난 결과(같은 배열 객체)를 돌려준다.
 */
export function wordSpans(text, words) {
  if (typeof text !== 'string' || !Array.isArray(words) || words.length === 0) return null;
  const hit = cache.get(words);
  if (hit && hit.text === text) return hit.spans;
  const spans = matchSpans(text, words);
  cache.set(words, { text, spans });
  return spans;
}

/**
 * @param {Array} spans wordSpans 결과
 * @param {number} sec 재생 위치(초)
 * @returns {number} start <= sec 인 가장 큰 인덱스 — 다음 낱말이 시작할 때까지 앞 낱말에 머문다.
 *   첫 낱말 전·spans 가 없음·sec 가 유한수가 아니면 -1. start 는 비내림이라 이분 탐색한다.
 */
export function activeWordIndex(spans, sec) {
  if (!Array.isArray(spans) || spans.length === 0 || !Number.isFinite(sec)) return -1;
  let lo = 0;
  let hi = spans.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const start = spans[mid] ? spans[mid].start : NaN;
    if (start <= sec) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}
